import { useState, useEffect } from 'react';
import axios from 'axios';
import { MousePointerClick, Users, Briefcase, ChevronRight, DollarSign, FlaskConical } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const DEMO_DATA = { clicks: 1200, demos: 120, closed_won: 18, revenue: 47500 };

const ConversionFunnel = ({ token }) => {
  const [real, setReal] = useState(null);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/business/funnel`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setReal(res.data))
      .catch(() => {});
  }, [token]);

  if (!real) return null;

  const data = demoMode ? DEMO_DATA : real;
  const hasAnyData = data.clicks > 0 || data.demos > 0 || data.closed_won > 0;
  const isIncomplete = data.clicks === 0 && (data.demos > 0 || data.closed_won > 0);

  const rate = (num, denom) => {
    if (denom <= 0) return null;
    return `${((num / denom) * 100).toFixed(0)}%`;
  };

  const stages = [
    { label: 'Clicks', value: data.clicks, icon: MousePointerClick, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20' },
    { label: 'Demos', value: data.demos, icon: Users, color: 'text-purple-400', bg: 'bg-purple-400/10 border-purple-400/20' },
    { label: 'Closed Won', value: data.closed_won, icon: Briefcase, color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/20' },
  ];

  const convRates = [
    rate(data.demos, data.clicks),
    rate(data.closed_won, data.demos),
  ];

  const revenue = data.revenue || 0;

  return (
    <div className="rounded-xl border border-white/10 bg-[#111317] p-5 mb-6" data-testid="conversion-funnel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <h3 className="text-sm font-semibold text-brand-text">Conversion Funnel</h3>
          {demoMode && (
            <span className="text-[9px] font-medium text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded border border-amber-400/20">
              Sample Data
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {revenue > 0 && (
            <span className="flex items-center gap-1 text-xs font-bold text-brand-gold">
              <DollarSign className="w-3 h-3" />{revenue.toLocaleString()} revenue
            </span>
          )}
          <button
            onClick={() => setDemoMode(prev => !prev)}
            data-testid="funnel-demo-toggle"
            className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border transition-colors ${
              demoMode
                ? 'bg-amber-400/10 border-amber-400/30 text-amber-400'
                : 'bg-zinc-800/50 border-zinc-700 text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <FlaskConical className="w-3 h-3" />
            {demoMode ? 'Live Data' : 'Demo'}
          </button>
        </div>
      </div>

      {/* Funnel Stages */}
      <div className="flex items-center justify-between">
        {stages.map((stage, i) => {
          const Icon = stage.icon;
          return (
            <div key={stage.label} className="flex items-center flex-1">
              <div className={`flex-1 rounded-lg border ${stage.bg} px-4 py-3 text-center`} data-testid={`funnel-${stage.label.toLowerCase().replace(/\s/g, '-')}`}>
                <Icon className={`w-5 h-5 mx-auto mb-1.5 ${stage.color}`} />
                <div className="text-2xl font-bold text-brand-text leading-none">{stage.value.toLocaleString()}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{stage.label}</div>
              </div>
              {i < stages.length - 1 && (
                <div className="flex flex-col items-center px-2 flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                  {convRates[i] ? (
                    <span className="text-[9px] text-zinc-400 mt-0.5 font-medium">{convRates[i]}</span>
                  ) : (
                    <span className="text-[9px] text-zinc-600 mt-0.5">—</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Helper Messages */}
      {!demoMode && isIncomplete && (
        <p className="text-[10px] text-zinc-600 mt-3 text-center" data-testid="funnel-incomplete-msg">
          Click tracking data will populate as salespeople share their tracking links
        </p>
      )}
      {!demoMode && !hasAnyData && (
        <p className="text-[10px] text-zinc-600 mt-3 text-center" data-testid="funnel-empty-msg">
          Data will populate as traffic increases
        </p>
      )}
    </div>
  );
};

export default ConversionFunnel;
